import { useTranslation } from "react-i18next";

import { money } from "../lib/format";
import { MARKETPLACE_LABEL } from "../lib/marketplace-labels";
import type { Order } from "../lib/types";

const EPS = 0.011;

export function SettlementCard({ order }: { order: Order }) {
  const { t } = useTranslation("orders");
  const s = order.settlement;
  const mkpLabel = MARKETPLACE_LABEL[order.mkp] ?? order.mkp;

  return (
    <div
      data-testid="stl-card"
      className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]"
    >
      <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
        <h3 className="font-semibold text-[var(--text-primary)]">{t("stl_title")}</h3>
        {s?.invariant_ok && (
          <span className="inline-flex items-center rounded-md bg-[var(--intent-success-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--intent-success-fg)]">
            {t("inv_ok")}
          </span>
        )}
      </div>
      <div className="px-5 py-3 text-sm">
        {!s ? (
          <p data-testid="stl-none" className="py-4 text-center text-[var(--text-subtle)]">
            {t("stl_none", { mkp: mkpLabel })}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Row
              label={t("stl_buyer_paid")}
              testId="stl-buyer-paid"
              value={s.buyer_paid}
              {...(order.currency !== undefined && { currency: order.currency })}
              {...(Math.abs(s.buyer_paid - order.money.total) >= EPS && {
                warn: t("stl_gross_mismatch"),
              })}
            />
            <Row
              label={t("stl_fees")}
              testId="stl-fees"
              value={s.marketplace_fees}
              sign="−"
              {...(order.currency !== undefined && { currency: order.currency })}
            />
            <div className="flex items-center justify-between border-t border-[var(--premium-border)] pt-2 font-semibold">
              <span>{t("stl_net_payout")}</span>
              <span className="font-mono" data-testid="stl-net">
                {money(s.net_payout, order.currency)}
              </span>
            </div>
            {s.settled_at && (
              <p className="pt-1 text-xs text-[var(--text-subtle)]">
                {t("stl_settled_at", { date: new Date(s.settled_at).toLocaleDateString() })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  currency,
  testId,
  sign = "",
  warn,
}: {
  label: string;
  value: number;
  currency?: string;
  testId: string;
  sign?: "" | "−";
  warn?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-1 text-[var(--text-subtle)]">
        {label}
        {warn && (
          <span
            data-testid="stl-gross-mismatch"
            title={warn}
            className="text-[var(--intent-warning-fg)]"
          >
            ⚠
          </span>
        )}
      </span>
      <span className="font-mono" data-testid={testId}>
        {sign ? `${sign} ` : ""}
        {money(value, currency)}
      </span>
    </div>
  );
}
